import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, shareReplay, finalize } from 'rxjs';
import { environment } from '../../environments/environment';

interface LoginResponse {
  tokenPCT: string;
  refreshTokenPCT: string;
}

@Injectable({ providedIn: 'root' })
export class AuthJwtService {
  private http = inject(HttpClient);

  private readonly TOKEN_KEY = 'tokenPCT';
  private readonly REFRESH_KEY = 'refreshTokenPCT';

  private refreshInFlight$: Observable<LoginResponse> | null = null;

  login(user: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${environment.urlAPI}Login`, { User: user, Password: password }).pipe(
      tap(res => {
        localStorage.setItem(this.TOKEN_KEY, res.tokenPCT);
        localStorage.setItem(this.REFRESH_KEY, res.refreshTokenPCT);
      })
    );
  }

  refreshToken(): Observable<LoginResponse> {
    const token = this.getToken();
    const refreshToken = this.getRefreshToken();
    return this.http.post<LoginResponse>(`${environment.urlAPI}Login/Refresh`, { Token: token, RefreshToken: refreshToken }).pipe(
      tap(res => {
        localStorage.setItem(this.TOKEN_KEY, res.tokenPCT);
        localStorage.setItem(this.REFRESH_KEY, res.refreshTokenPCT);
      })
    );
  }

  /**
   * El backend rota el refresh token en cada uso (compara por igualdad exacta contra el guardado
   * en BBDD). Si dos peticiones expiran a la vez y cada una llama a refreshToken() por su cuenta,
   * la segunda envía un refresh token ya "gastado" por la primera y el backend la rechaza,
   * cerrando la sesión aunque el refresh ya hubiera funcionado. Este método comparte una única
   * llamada de refresh en curso entre todas las peticiones que la necesiten a la vez.
   */
  refreshTokenShared(): Observable<LoginResponse> {
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = this.refreshToken().pipe(
        shareReplay(1),
        finalize(() => { this.refreshInFlight$ = null; })
      );
    }
    return this.refreshInFlight$;
  }

  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_KEY);
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_KEY);
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  getUserFromToken(): { name: string; roles: string[] } {
    const token = this.getToken();
    if (!token) return { name: '', roles: [] };

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const name = `${payload['firstName'] ?? ''} ${payload['lastName'] ?? ''}`.trim()
        || payload['mail'] || '';
      const rawRoles = payload['role'] ?? payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ?? [];
      const roles: string[] = Array.isArray(rawRoles) ? rawRoles : [rawRoles];
      return { name, roles };
    } catch {
      return { name: '', roles: [] };
    }
  }
}
